import Modal from "../../../../../assets/js/components/Modal";
import client from "libs/client";
import { parseMessageAttribute } from "libs/utils";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import AsyncSelect from "react-select/async";
import type { SingleValue } from "react-select";
import { useToast } from "ui-kit";
import type { TeamCompetitionEditionRowResponse } from "libs/apiClient";

type EditionOption = {
	value: number;
	label: string;
	competitionName?: string | null;
	competitionCountry?: string | null;
	seasonName?: string | null;
	groupLabel?: string | null;
	phaseLabel?: string | null;
};

type DiscoverTeamCompetitionsTableProps = {
	teamId: number;
	canEdit: boolean;
};

function formatEditionMeta(edition: {
	groupLabel?: string | null;
	phaseLabel?: string | null;
}): string {
	const parts: string[] = [];
	if (edition.groupLabel != null && edition.groupLabel !== "") {
		parts.push(edition.groupLabel);
	}
	if (edition.phaseLabel != null && edition.phaseLabel !== "") {
		parts.push(edition.phaseLabel);
	}
	if (parts.length === 0) {
		return "-";
	}
	return parts.join(" · ");
}

export function DiscoverTeamCompetitionsTable({
	teamId,
	canEdit,
}: DiscoverTeamCompetitionsTableProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [removeTarget, setRemoveTarget] = useState<TeamCompetitionEditionRowResponse | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: continentsResponse } = client.getContinentCountry.useQuery({ retry: false });

	const countryLabelsByCode = useMemo(() => {
		const labels: Record<string, string> = { INT: "International" };
		for (const continent of continentsResponse?.data ?? []) {
			for (const country of continent.options ?? []) {
				if (country.value != null && country.label != null) {
					labels[country.value] = country.label;
				}
			}
		}
		return labels;
	}, [continentsResponse?.data]);

	const {
		data: editionsResponse,
		isLoading,
		refetch,
	} = client.getTeamCompetitionEditions.useQuery(
		{ id: teamId },
		{ retry: false },
	);

	const addTeamsMutation = client.postCompetitionEditionTeams.useMutation();
	const removeTeamMutation = client.deleteCompetitionEditionTeam.useMutation();

	const editions = editionsResponse?.data?.items ?? [];

	const loadEditionOptions = useCallback(
		async (inputValue: string): Promise<EditionOption[]> => {
			const search = inputValue.trim();
			if (search === "") {
				return [];
			}

			const response = await client.getTeamCompetitionEditionOptions.query({
				id: teamId,
				search,
				limit: 20,
			});
			const items = response.data?.items ?? [];
			return items
				.filter((item) => item.id != null && item.label != null)
				.map((item) => ({
					value: item.id as number,
					label: item.label as string,
					competitionName: item.competitionName,
					competitionCountry: item.competitionCountry,
					seasonName: item.seasonName,
					groupLabel: item.groupLabel,
					phaseLabel: item.phaseLabel,
				}));
		},
		[teamId],
	);

	const handleAddEdition = useCallback(
		async (option: SingleValue<EditionOption>) => {
			if (option == null || canEdit === false || isSubmitting) {
				return;
			}

			setIsSubmitting(true);
			try {
				await addTeamsMutation.mutateAsync({
					id: option.value,
					body: { teamIds: [teamId] },
				});
				addSuccessToast("Team enrolled in competition edition");
				await refetch();
			} catch (error: unknown) {
				addErrorToast(parseMessageAttribute(error) || "Failed to add competition edition");
			} finally {
				setIsSubmitting(false);
			}
		},
		[addErrorToast, addSuccessToast, addTeamsMutation, canEdit, isSubmitting, refetch, teamId],
	);

	const closeRemoveModal = () => {
		setRemoveTarget(null);
		setIsSubmitting(false);
	};

	const handleRemove = async () => {
		if (removeTarget?.id == null || canEdit === false) {
			return;
		}

		setIsSubmitting(true);
		try {
			await removeTeamMutation.mutateAsync({
				id: removeTarget.id,
				teamId,
			});
			addSuccessToast("Team removed from competition edition");
			closeRemoveModal();
			await refetch();
		} catch (error: unknown) {
			addErrorToast(parseMessageAttribute(error) || "Failed to remove competition edition");
			setIsSubmitting(false);
		}
	};

	if (isLoading) {
		return <div className="empty-sub-table">Loading...</div>;
	}

	return (
		<>
			{canEdit ? (
				<div style={{ marginBottom: "1rem" }}>
					<AsyncSelect<EditionOption>
						className="react-select"
						classNamePrefix="react-select"
						cacheOptions
						defaultOptions={false}
						loadOptions={loadEditionOptions}
						value={null}
						onChange={handleAddEdition}
						placeholder="Search competition editions to add..."
						isClearable={false}
						isDisabled={isSubmitting}
						noOptionsMessage={({ inputValue }) => {
							if (inputValue.trim() === "") {
								return "Type to search competition editions";
							}
							return "No competition editions found";
						}}
						formatOptionLabel={(option) => {
							const countryPart =
								option.competitionCountry != null && option.competitionCountry !== ""
									? (countryLabelsByCode[option.competitionCountry] ??
										option.competitionCountry)
									: null;
							return (
								<div>
									<div>{option.competitionName ?? option.label}</div>
									<div style={{ fontSize: "0.8125rem", color: "#6b7280" }}>
										{[
											countryPart,
											option.seasonName,
											option.groupLabel,
											option.phaseLabel,
											`#${option.value}`,
										]
											.filter((part) => part != null && part !== "")
											.join(" · ")}
									</div>
								</div>
							);
						}}
					/>
				</div>
			) : null}

			{editions.length === 0 ? (
				<div className="empty-sub-table">No competition editions</div>
			) : (
				<div className="discover-team-groups-table">
					<table className="discover-table">
						<thead>
							<tr>
								<th>ID</th>
								<th>Competition</th>
								<th>Season</th>
								<th>Group / Phase</th>
								<th>Scout</th>
								<th>Dates</th>
								{canEdit ? <th>Actions</th> : null}
							</tr>
						</thead>
						<tbody>
							{editions.map((edition) => {
								const editionId = edition.id ?? 0;
								const editionKey = editionId > 0 ? editionId : edition.competitionName;
								const startDate =
									edition.startDate != null && edition.startDate !== ""
										? edition.startDate
										: null;
								const endDate =
									edition.endDate != null && edition.endDate !== "" ? edition.endDate : null;
								let datesLabel = "-";
								if (startDate != null && endDate != null) {
									datesLabel = `${startDate} – ${endDate}`;
								} else if (startDate != null) {
									datesLabel = `From ${startDate}`;
								} else if (endDate != null) {
									datesLabel = `Until ${endDate}`;
								}

								return (
									<tr key={editionKey}>
										<td>
											<Link to={`/product-management/competitions/editions/${editionId}`}>
												{editionId}
											</Link>
										</td>
										<td>
											<Link to={`/product-management/competitions/editions/${editionId}`}>
												{edition.competitionName ?? "-"}
											</Link>
										</td>
										<td>{edition.seasonName ?? "-"}</td>
										<td>{formatEditionMeta(edition)}</td>
										<td>{edition.isScoutVisible === false ? "Hidden" : "Visible"}</td>
										<td>{datesLabel}</td>
										{canEdit ? (
											<td>
												<button
													type="button"
													className="button-link"
													disabled={isSubmitting}
													onClick={() => setRemoveTarget(edition)}
												>
													Remove
												</button>
											</td>
										) : null}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{removeTarget != null ? (
				<Modal
					show={true}
					title="Remove from competition edition"
					size="sm"
					onClose={() => {
						if (isSubmitting === false) {
							closeRemoveModal();
						}
					}}
				>
					<p style={{ marginBottom: "1.5rem" }}>
						Do you want to remove this team from{" "}
						<strong>
							{removeTarget.competitionName}
							{removeTarget.seasonName != null && removeTarget.seasonName !== ""
								? ` · ${removeTarget.seasonName}`
								: ""}
						</strong>
						?
					</p>
					<div className="admin-buttons admin-buttons--right">
						<button
							type="button"
							className="admin-button admin-button--primary"
							disabled={isSubmitting}
							onClick={handleRemove}
						>
							Remove
						</button>
						<button
							type="button"
							className="admin-button admin-button--secondary"
							disabled={isSubmitting}
							onClick={closeRemoveModal}
						>
							Cancel
						</button>
					</div>
				</Modal>
			) : null}
		</>
	);
}
